import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './StudentSessions.css';

const storageKey = 'upskill-student-session';
const endpoint = 'https://australia-southeast1-upskillinstitute-au.cloudfunctions.net/studentSessionPortal';
export async function portalRequest(action, body = {}, token = '') {
  const response = await fetch(endpoint, {
    method: 'POST', headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify({ ...body, action }),
  });
  const data = await response.json();
  if (!response.ok) throw Object.assign(new Error(data.error || 'Unable to continue. Please try again.'), { status: response.status });
  return data;
}

export default function StudentSessions() {
  const navigate = useNavigate();
  const [token, setToken] = useState(() => {
    try { return sessionStorage.getItem(storageKey) || ''; } catch (_) { return ''; }
  });
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [challengeId, setChallengeId] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [sessions, setSessions] = useState(null);
  const [refresh, setRefresh] = useState(0);

  useEffect(() => {
    if (!token) return;
    let active = true;
    setBusy(true); setError('');
    portalRequest('list', {}, token).then(data => {
      if (active) { setSessions(data.sessions); setEmail(data.email); }
    }).catch(err => {
      if (!active) return;
      setError(err.message);
      if (err.status === 401) { sessionStorage.removeItem(storageKey); setToken(''); setSessions(null); }
    }).finally(() => { if (active) setBusy(false); });
    return () => { active = false; };
  }, [token, refresh]);

  const requestCode = async event => {
    event.preventDefault(); setBusy(true); setError(''); setMessage('');
    try {
      const data = await portalRequest('request', { email });
      setChallengeId(data.challengeId); setCode(''); setMessage(data.message);
    } catch (err) { setError(err.message); }
    finally { setBusy(false); }
  };
  const verify = async event => {
    event.preventDefault(); setBusy(true); setError('');
    try {
      const data = await portalRequest('verify', { challengeId, code });
      sessionStorage.setItem(storageKey, data.token); setToken(data.token); setChallengeId(''); setCode(''); setMessage('');
    } catch (err) { setError(err.message); }
    finally { setBusy(false); }
  };
  const exit = async () => {
    setBusy(true); setError('');
    try {
      await portalRequest('logout', {}, token);
      sessionStorage.removeItem(storageKey); setToken(''); setSessions(null); setEmail(''); setCode(''); setChallengeId('');
      navigate('/links', { replace: true });
    } catch (err) { setError('Unable to exit securely. Please try again.'); }
    finally { setBusy(false); }
  };
  return <main className="studentSessions">
    <header><div><p className="studentSessionsEyebrow">Upskill Institute</p><h1>{token ? 'My submissions' : 'Log back in'}</h1></div>
      {token ? <button type="button" disabled={busy} onClick={exit}>Exit student login</button> : <Link to="/links">Back to links</Link>}
    </header>
    {error && <p role="alert" className="studentSessionsError">{error}</p>}
    {message && <p role="status">{message}</p>}
    {!token ? <section className="studentSessionsCard">
      <p>Use the email address on your submitted EOI to find your saved assessments.</p>
      {!challengeId ? <form onSubmit={requestCode}>
        <label>Email address<input type="email" autoComplete="email" required maxLength={254} value={email} disabled={busy} onChange={event => setEmail(event.target.value)} /></label>
        <button disabled={busy} type="submit">{busy ? 'Sending...' : 'Send login code'}</button>
      </form> : <form onSubmit={verify}>
        <p>Enter the code sent to {email}. It expires after 10 minutes.</p>
        <label>Six-digit code<input autoComplete="one-time-code" inputMode="numeric" pattern="[0-9]{6}" maxLength={6} required value={code} disabled={busy} onChange={event => setCode(event.target.value.replace(/\D/g, ''))} /></label>
        <button disabled={busy} type="submit">{busy ? 'Checking...' : 'Verify and view submissions'}</button>
        <button disabled={busy} type="button" onClick={requestCode}>Resend code</button>
        <button disabled={busy} type="button" onClick={() => { setChallengeId(''); setCode(''); setMessage(''); setError(''); }}>Use another email</button>
      </form>}
    </section> : <section>
      <p>Signed in as {email}. Only previously saved progress can be restored.</p>
      <button type="button" disabled={busy} onClick={() => setRefresh(value => value + 1)}>Refresh submissions</button>
      {busy && <p role="status">Loading...</p>}
      {sessions?.length === 0 && <p>No submitted EOIs are currently linked to this email. Contact your trainer if a course is missing.</p>}
      <div className="studentSessionsList">{sessions?.map(session => <article className="studentSessionsCard" key={session.id}>
        <div><h2>{session.course}</h2><p>{session.date ? String(session.date).slice(0, 10) : 'Date not recorded'}</p></div>
        <div><span className={session.status === 'Completed' ? 'studentSessionsCompleted' : ''}>{session.status}</span>
          {session.status !== 'Completed' && session.continuePath && <Link className="studentSessionsContinue" to={session.continuePath}>Continue</Link>}
          {session.status !== 'Completed' && !session.continuePath && <p>No online assessment session is linked. Contact your trainer for the next step.</p>}
        </div>
      </article>)}</div>
    </section>}
  </main>;
}
